export type ScreenRouteEntry = { parent?: string; key: string };
export type ScreenRouteMapping = Record<string, ScreenRouteEntry>;

export interface RuntimeParams {
  bookingPageUrl: string;
  posManagerUrl: string;
}

interface ScreenDefinition {
  parent?: string;
  routeSegments: string[];
  menuKey: (p: RuntimeParams) => string;
}

const screenDefinitions: Record<string, ScreenDefinition> = {
  calendar: {
    routeSegments: ['calendar'],
    menuKey: () => '/calendar',
  },
  'shore-pos': {
    routeSegments: ['shore-pos'],
    menuKey: (p) => p.posManagerUrl,
  },
  'booking-page': {
    routeSegments: ['booking-page'],
    menuKey: (p) => p.bookingPageUrl,
  },
  'shift-plan': {
    routeSegments: ['shift-plan'],
    menuKey: () => '/shiftplan',
  },
  'management/customers': {
    parent: 'management',
    routeSegments: ['customers'],
    menuKey: () => '/customers',
  },
  'management/resources': {
    parent: 'management',
    routeSegments: ['resources'],
    menuKey: () => '/resources',
  },
  'management/employees': {
    parent: 'management',
    routeSegments: ['hr'],
    menuKey: () => '/hr',
  },
  'management/services': {
    parent: 'management',
    routeSegments: ['services'],
    menuKey: () => '/services',
  },
  'marketing/messenger': {
    parent: 'marketing',
    routeSegments: ['messenger'],
    menuKey: () => '/services#/messenger',
  },
  'marketing/newsletter': {
    parent: 'marketing',
    routeSegments: ['newsletter'],
    menuKey: () => '/newsletter',
  },
  'marketing/feedback': {
    parent: 'marketing',
    routeSegments: ['feedback'],
    menuKey: () => '/feedback',
  },
  'marketing/reminders': {
    parent: 'marketing',
    routeSegments: ['reminders'],
    menuKey: () => '/reminders',
  },
  'analysis/dashboard': {
    parent: 'analysis',
    routeSegments: ['insights'],
    menuKey: () => '/insights',
  },
  'analysis/transactions': {
    parent: 'analysis',
    routeSegments: ['transactions'],
    menuKey: () => '/transactions',
  },
  'analysis/reports': {
    parent: 'analysis',
    routeSegments: ['pos-reports-v2'],
    menuKey: () => '/pos-reports-v2',
  },
  'setup/application-settings': {
    parent: 'setup',
    routeSegments: ['application-settings'],
    menuKey: () => '/application-settings',
  },
  'setup/settings': {
    parent: 'setup',
    routeSegments: ['settings'],
    menuKey: () => '/settings',
  },
  'setup/locations': {
    parent: 'setup',
    routeSegments: ['locations'],
    menuKey: () => '/locations',
  },
};

function findScreenByRouteSegment(segment: string): string | undefined {
  const match = Object.entries(screenDefinitions).find(([, def]) =>
    def.routeSegments.includes(segment)
  );

  return match?.[0];
}

export function getScreenNameFromRoute(routeFragment: string): string {
  const exactMatch = findScreenByRouteSegment(routeFragment);
  if (exactMatch) {
    return exactMatch;
  }

  // Hash routes are matched against the menu item's `key` verbatim (the
  // messenger item's key *is* `#/messenger`), so they must pass through as-is.
  if (routeFragment.startsWith('#')) {
    return routeFragment;
  }

  // A sub-route belongs to the same screen as its leading segment: the menu
  // highlights a screen, not a sub-view, so `calendar/week` has to resolve to
  // `calendar`. Split on `/` and `?` only — a fragment like `services#/messenger`
  // keeps its `#` and so still falls through unmatched, as it did before.
  const leadingSegment = routeFragment.split(/[/?]/)[0];
  if (leadingSegment === routeFragment) {
    return routeFragment;
  }

  return findScreenByRouteSegment(leadingSegment) ?? routeFragment;
}

export function buildScreenRouteMapping(
  params: RuntimeParams
): ScreenRouteMapping {
  return Object.entries(screenDefinitions).reduce<ScreenRouteMapping>(
    (acc, [screenName, def]) => {
      acc[screenName] = {
        parent: def.parent,
        key: def.menuKey(params),
      };
      return acc;
    },
    {}
  );
}

export { screenDefinitions };
